import { isServer } from '@bob/library'
import { getUser } from '@websrc/App/Helpers/authManager'
import { CoralProductMetaState, ProductMetaState } from '@websrc/Basket/useProductMeta'
import { mapCoralProductPage, mapProductPage } from '@websrc/DataMappers/mapProductPage'
import React, { useEffect, useState } from 'react'

import { priceStringToNumber } from '../Helpers/formatting'
import { getSplitTestCookie } from '../Helpers/getSplitTestCookie'
import { isUnderTest } from '../Helpers/isUnderTest'
import { useHasConsent } from '../Hooks/useHasConsent/useHasConsent'
import { useScript } from '../Hooks/useScript/useScript'

const customDimensionsIds = {
  pageTemplate: 1,
  pageName: 3,
  searchQuery: 4,
  searchResultNumber: 5,
  envLanguage: 6,
  envCountry: 7,
  loginStatus: 8,
  transactionType: 9,
  locale: 10,
  branch: 12,
  splitTest: 13,
}

export type MatomoClickedElement =
  | 'menu'
  | 'text'
  | 'icon'
  | 'button'
  | 'product'
  | 'drop-down list'
  | 'check box'
  | 'radio button'
  | 'block'
  | 'form'
  | 'filter'
  | 'link'
  | 'accordion'
  | 'card'

export const matomo = {
  push: (args: [string, ...any[]]) => {
    if (isUnderTest()) return
    window._paq = window._paq || []
    window._paq.push(args)
  },
  setCustomDimension: (name: keyof typeof customDimensionsIds, value: string | number) => {
    if (isUnderTest()) return
    matomo.push(['setCustomDimension', customDimensionsIds[name], value])
  },
  setEcommerceView: (
    productNumber: string,
    productName: string,
    productType: string,
    productPrice?: string
  ) => {
    if (isUnderTest()) return
    matomo.push([
      'setEcommerceView',
      productNumber,
      productName,
      productType ? productType.toLowerCase() : undefined,
      productPrice ? priceStringToNumber(productPrice) : undefined,
    ])
  },
  clearEcommerceView: () => {
    if (isUnderTest()) return
    matomo.push(['setEcommerceView'])
  },
  trackCoralEvent: (data: {
    eventCategory: 'sign-up'
    eventAction: 'open-modal' | 'close-modal' | 'form-submit'
    eventName: string
  }) => {
    if (isUnderTest()) return

    matomo.push(['trackEvent', data.eventCategory, data.eventAction, data.eventName])
  },
  trackEvent: (
    category: 'engagement' | 'loyalty' | 'conversion',
    pageTemplate: string,
    position: (string | undefined | null)[],
    description: (string | undefined | null)[],
    element?: MatomoClickedElement
  ) => {
    if (isUnderTest()) return
    const eventName = [pageTemplate]

    const elementPosition = position
      .filter<string>((pos): pos is string => Boolean(pos))
      .map((pos) => pos.toLowerCase())
      .join(' > ')

    if (elementPosition) {
      eventName.push(elementPosition)
    }

    const eventDescription = []
    const action = element === 'form' ? 'submission' : 'click'

    if (element) {
      eventDescription.push(`${action} ${element}`)
    }

    const elementDescription = description
      .filter<string>((pos): pos is string => Boolean(pos))
      .map((pos) => pos.toLowerCase())
      .join(' | ')

    if (elementDescription) {
      eventDescription.push(elementDescription)
    }

    matomo.push(['trackEvent', category, eventName.join(' | '), eventDescription.join(' | ')]) // matomo.push(['trackEvent', category, action, name])
  },
  updateUrl: () => {
    if (isUnderTest()) return
    matomo.push(['setCustomUrl', `${location.pathname}${location.search}${location.hash}`])
  },
}

export interface MatomoProps {
  path: string
  title: string
  productMetaState: ProductMetaState
  coralProductMetaState: CoralProductMetaState
  pageTemplate: string
  productDetails?: ReturnType<typeof mapProductPage | typeof mapCoralProductPage>['product_details']
  productCategory?: string
  callTrackPageView?: boolean
  locale: string
}

const addUaAndLang = () => {
  const userAgent = window.navigator.userAgent

  let params = `&ua=${userAgent}`

  // Use same format as the "Accept-Language" header
  // Sentry has recorded some exceptions related to naviagator.languages being undefined
  const browserLanguages =
    window.navigator.languages?.reduce((prev, curr, index, arr) => {
      if (index > 0) {
        curr += `;q=${Math.max(1 - index / 10, 0.1)}`
      }

      if (index !== arr.length - 1) {
        curr += ','
      }

      return prev + curr
    }, '') ?? window.navigator.language

  if (browserLanguages) {
    params += `&lang=${browserLanguages}`
  }

  return params
}

export const Matomo: React.FC<MatomoProps> = ({
  path,
  title,
  productDetails,
  productMetaState,
  coralProductMetaState,
  pageTemplate,
  productCategory,
  callTrackPageView = true,
  locale,
}) => {
  const hasConsent = useHasConsent(['analytics'])
  const [pluginAdded, setPluginAdded] = useState(false)

  useEffect(() => {
    if (isUnderTest()) return
    // These need to be set before the matomo script is loaded
    matomo.push(['requireCookieConsent'])
    matomo.push(['enableLinkTracking'])
    matomo.push(['setTrackerUrl', '/api/t'])
    matomo.push(['setSiteId', process.env.MATOMO_SITE_ID])
  }, [])

  // Script is generated by Matomo (see our Matomo repo)
  const matomoScriptState = useScript(
    '/scripts/i-2023-07-21.js',
    Boolean(process.env.MATOMO_ENABLED) && !isServer
  )

  useEffect(() => {
    if (isUnderTest()) return
    if (matomoScriptState !== 'loaded') {
      return
    }

    /* Add plugin that adds 'ua' and 'lang' (user-agent and accept-language headers) to the query parameters of events.
        This is needed because the requests go through cloudfront before hitting the matomo proxy
        and cloudfront overwrites these headers. */
    window.Matomo?.addPlugin('SetUaAndLang', {
      event: addUaAndLang,
      goal: addUaAndLang,
      link: addUaAndLang,
      log: addUaAndLang,
      ecommerce: addUaAndLang,
    })

    setPluginAdded(true)
  }, [matomoScriptState])

  useEffect(() => {
    if (isUnderTest()) return
    // We need to wait for the plugin to be added before we start sending events
    if (!pluginAdded) {
      return
    }

    matomo.updateUrl()
    matomo.push(['setDocumentTitle', title])

    const siteUrl = process.env.SITE_URL
    matomo.push(['setDomains', siteUrl ? [siteUrl.replace('https://', '*.')] : []])

    if (productDetails) {
      const productData = productMetaState[productDetails.product_number]
        ? productMetaState[productDetails.product_number]
        : coralProductMetaState[productDetails.product_number]

      const { name, product_number } = productData
      const type = 'type' in productData ? (productData.type as string) : 'unknown'

      matomo.setEcommerceView(product_number, name, type, productDetails.price_discount)
    } else if (productCategory) {
      matomo.setEcommerceView('', '', productCategory)
    } else {
      // Clear eCommerce view
      matomo.clearEcommerceView()
    }

    matomo.setCustomDimension('pageTemplate', pageTemplate)
    matomo.setCustomDimension('pageName', title)

    matomo.setCustomDimension(
      'searchQuery',
      new URLSearchParams(window.location.search).get('search') ?? ''
    )

    const [language, country] = window.navigator.language.split('-')

    matomo.setCustomDimension('envLanguage', language)

    matomo.setCustomDimension('envCountry', country?.toUpperCase()) // in older versions of Safari, it is in lower case

    void getUser().then((user) => {
      matomo.setCustomDimension('loginStatus', user ? 'logged in' : 'not logged in')

      if (callTrackPageView) {
        matomo.push(['trackPageView'])
      }
    })

    matomo.setCustomDimension('locale', locale)
    matomo.setCustomDimension('branch', String(process.env.BRANCH))

    interface SplitTestEntry {
      name: string
      variant: string
    }

    const splitTestCookie = getSplitTestCookie()
    if (splitTestCookie) {
      const splitTests = JSON.parse(splitTestCookie)

      let dimensionString = ''

      splitTests.map((test: SplitTestEntry) => {
        dimensionString = dimensionString + `${test.name}=${test.variant};`
      })

      matomo.setCustomDimension('splitTest', dimensionString)
    }
  }, [
    path,
    title,
    productDetails,
    productMetaState,
    pluginAdded,
    pageTemplate,
    productCategory,
    callTrackPageView,
    locale,
    coralProductMetaState,
  ])

  useEffect(() => {
    if (isUnderTest()) return
    if (hasConsent) {
      matomo.push(['rememberCookieConsentGiven'])
      void getUser().then((user) => matomo.push(['setUserId', user?.profile.sub ?? '']))
    } else {
      matomo.push(['forgetCookieConsentGiven'])
      matomo.push(['resetUserId'])
    }
  }, [hasConsent])

  return null
}
